/**
 * Assign Worklist Modal
 *
 * Modal for assigning a worklist item to a radiologist
 * Shows radiologists with their current workload
 */

import { useState } from 'react'
import { Loader2, User, Clock } from 'lucide-react'
import { useRadiologists, sortByWorkload, getTotalWorkload } from '@/application/users'
import { useAssignWorklistItem } from '@/application/worklist'
import { toast } from '@/lib/toast'
import type { WorklistItem } from '@/domain/worklist'
import { Button } from '@/presentation/components/ui/Button'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  DialogClose,
} from '@/presentation/components/ui/Dialog'
import { PRIORITY_LABELS, WORKLIST_STATUS_LABELS, type PriorityLevel } from '@/config/constants'

// =============================================================================
// Types
// =============================================================================

interface AssignWorklistModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  item: WorklistItem | null
  onSuccess?: () => void
}

// =============================================================================
// Component
// =============================================================================

export function AssignWorklistModal({
  open,
  onOpenChange,
  item,
  onSuccess,
}: AssignWorklistModalProps) {
  const [selectedRadiologistId, setSelectedRadiologistId] = useState<string>('')
  const [priority, setPriority] = useState<PriorityLevel>(3)
  const [notes, setNotes] = useState<string>('')

  // Only fetch radiologists when modal is open
  const { data: radiologists, isLoading: isLoadingRadiologists } = useRadiologists({ enabled: open })
  const assignMutation = useAssignWorklistItem()

  const sortedRadiologists = radiologists ? sortByWorkload(radiologists) : []

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!item || !selectedRadiologistId) return

    try {
      await assignMutation.mutateAsync({
        id: item.id,
        data: {
          assignedTo: selectedRadiologistId,
          priority,
          // dueAt se calcula automáticamente según el tiempo de respuesta de la institución
          notes: notes || undefined,
        },
      })

      // Reset form
      setSelectedRadiologistId('')
      setPriority(3)
      setNotes('')

      toast.success('Estudio asignado correctamente')
      onOpenChange(false)
      onSuccess?.()
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Error al asignar estudio')
    }
  }

  const handleClose = () => {
    setSelectedRadiologistId('')
    setPriority(3)
    setNotes('')
    onOpenChange(false)
  }

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogClose onClose={handleClose} />
        <DialogHeader>
          <DialogTitle>Asignar Estudio</DialogTitle>
          <DialogDescription>
            Seleccione un radiologo para asignar este estudio
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4 mt-4">
          {/* Radiologist Selection */}
          <div>
            <label className="block text-sm font-medium text-[var(--foreground)] mb-2">
              Radiologo <span className="text-red-500">*</span>
            </label>
            {isLoadingRadiologists ? (
              <div className="flex items-center justify-center p-4">
                <Loader2 className="h-5 w-5 animate-spin" />
              </div>
            ) : sortedRadiologists.length === 0 ? (
              <p className="text-sm text-[var(--foreground-muted)]">
                No hay radiologos disponibles
              </p>
            ) : (
              <div className="space-y-2 max-h-48 overflow-y-auto border rounded-md p-2">
                {sortedRadiologists.map((radiologist) => {
                  const workload = getTotalWorkload(radiologist)
                  const isSelected = selectedRadiologistId === radiologist.id

                  return (
                    <button
                      key={radiologist.id}
                      type="button"
                      onClick={() => setSelectedRadiologistId(radiologist.id)}
                      className={
                        'w-full flex items-center justify-between p-3 rounded-md border transition-colors ' +
                        (isSelected
                          ? 'border-[var(--primary)] bg-[var(--primary)]/5'
                          : 'border-[var(--border)] hover:border-[var(--primary)]/50 hover:bg-[var(--background-subtle)]')
                      }
                    >
                      <div className="flex items-center gap-3">
                        <div className="p-2 bg-[var(--background-subtle)] rounded-full">
                          <User className="h-4 w-4 text-[var(--foreground-muted)]" />
                        </div>
                        <div className="text-left">
                          <p className="font-medium text-sm">{radiologist.fullName}</p>
                          <p className="text-xs text-[var(--foreground-muted)]">
                            {radiologist.pendingCount} {WORKLIST_STATUS_LABELS.PENDING.toLowerCase()} - {radiologist.inProgressCount} {WORKLIST_STATUS_LABELS.IN_PROGRESS.toLowerCase()}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-1">
                        <Clock className="h-3 w-3 text-[var(--foreground-muted)]" />
                        <span className={
                          'text-sm font-medium ' +
                          (workload === 0
                            ? 'text-green-600 dark:text-green-400'
                            : workload < 5
                              ? 'text-yellow-600 dark:text-yellow-400'
                              : 'text-red-600 dark:text-red-400')
                        }>
                          {workload}
                        </span>
                      </div>
                    </button>
                  )
                })}
              </div>
            )}
          </div>

          {/* Priority */}
          <div>
            <label className="block text-sm font-medium text-[var(--foreground)] mb-2">
              Prioridad
            </label>
            <select
              value={priority}
              onChange={(e) => setPriority(parseInt(e.target.value, 10) as PriorityLevel)}
              className="w-full h-10 rounded-md border border-[var(--border)] bg-[var(--background)] px-3 text-sm"
            >
              {Object.entries(PRIORITY_LABELS).map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>
          </div>

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-[var(--foreground)] mb-2">
              Notas (opcional)
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Instrucciones especiales..."
              className="w-full rounded-md border border-[var(--border)] bg-[var(--background)] px-3 py-2 text-sm resize-none"
              rows={3}
            />
          </div>

          <DialogFooter className="mt-6">
            <Button type="button" variant="outline" onClick={handleClose}>
              Cancelar
            </Button>
            <Button
              type="submit"
              disabled={!selectedRadiologistId || assignMutation.isPending}
            >
              {assignMutation.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  Asignando...
                </>
              ) : (
                'Asignar'
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
